import { lazy, type ComponentType } from 'react';

/**
 * lazyWithRetry -- Drop-in replacement for React.lazy that handles
 * stale chunk errors after deployment.
 *
 * Problem: After a deploy, JS chunk hashes change. Users with a cached
 * index.html try to load old chunk URLs that no longer exist, causing
 * "Failed to fetch dynamically imported module" errors that crash React.
 *
 * Solution: Catch the import error, reload the page once to fetch the
 * fresh index.html with correct chunk references. A sessionStorage flag
 * prevents infinite reload loops if the error persists.
 *
 * Usage: Replace `lazy(() => import('./Page'))` with
 *        `lazyWithRetry(() => import('./Page'))`
 */

const RELOAD_FLAG = 'amalq_chunk_retry';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyImportFn = () => Promise<{ default: ComponentType<any> }>;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    // Vite/Rollup chunk load failure
    message.includes('failed to fetch dynamically imported module') ||
    // Webpack-style chunk load failure
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    // Generic network failures on JS files
    message.includes('error loading dynamically imported module') ||
    // Safari-specific
    message.includes('unexpected token') ||
    // Chrome network error
    (error.name === 'TypeError' && message.includes('failed to fetch'))
  );
}

export function lazyWithRetry(importFn: LazyImportFn) {
  return lazy(() =>
    importFn().catch((error: unknown) => {
      // Only attempt reload for chunk load errors (not syntax errors, etc.)
      if (!isChunkLoadError(error)) {
        throw error;
      }

      // Check if we already retried this session to prevent infinite loops
      const hasRetried = sessionStorage.getItem(RELOAD_FLAG);

      if (!hasRetried) {
        // Mark that we're retrying so we don't loop
        sessionStorage.setItem(RELOAD_FLAG, '1');
        // Reload the page to get fresh index.html with correct chunk hashes
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering
        // while the page reloads
        return new Promise(() => {});
      }

      // Already retried once this session -- don't loop, let the error
      // bubble to the error boundary
      sessionStorage.removeItem(RELOAD_FLAG);
      throw error;
    })
  );
}

/**
 * Clear the retry flag on successful page load.
 * Call this once in the app root (e.g., App.tsx useEffect).
 * This ensures the retry mechanism works again after a successful load.
 */
export function clearChunkRetryFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}
